/**
 * 🚨 UnstickGuildPage — direct-URL emergency escape when the Guild page
 * is unreachable (modal overlays blocking clicks, Rebirth popups, etc.).
 *
 * Just navigating to `/unstick-guild` fires `POST /api/guild/leave` on
 * mount — no buttons to click, no overlays in the way. Shows a tiny
 * "working / done / error" status, then auto-redirects to /guild.
 *
 * Shipped: Sep 2026 (Roc support — Brice Brody stuck in "Undefeated").
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnstickGuildPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("working"); // working | done | error
  const [detail, setDetail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/guild/leave`, {}, { withCredentials: true });
        if (cancelled) return;
        setStatus("done");
        setDetail(r?.data?.dissolved ? "Dead guild dissolved." : "Left the guild.");
        setTimeout(() => navigate("/guild", { replace: true }), 1800);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message || "Something went wrong.";
        setStatus(msg.toLowerCase().includes("not in a guild") ? "done" : "error");
        setDetail(msg);
        if (!msg.toLowerCase().includes("not in a guild")) return;
        setTimeout(() => navigate("/guild", { replace: true }), 1800);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const toneBg = {
    working: "bg-amber-950/40 border-amber-700/40 text-amber-200",
    done:    "bg-emerald-950/40 border-emerald-700/40 text-emerald-200",
    error:   "bg-red-950/40 border-red-700/40 text-red-200",
  }[status];

  const icon = { working: "⏳", done: "✅", error: "⚠️" }[status];
  const title = {
    working: "Freeing you from the guild…",
    done:    "You're free.",
    error:   "Couldn't force-leave.",
  }[status];

  return (
    <div className="min-h-screen bg-black text-neutral-200 flex items-center justify-center px-4"
         data-testid="unstick-guild-page">
      <div className={`max-w-md w-full rounded-2xl border p-8 text-center ${toneBg}`}>
        <div className="text-5xl mb-4" aria-hidden="true">{icon}</div>
        <h1 className="text-2xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
          {title}
        </h1>
        {detail && (
          <p className="text-sm opacity-80 mb-4 break-words" data-testid="unstick-detail">{detail}</p>
        )}
        <div className="mt-4 flex gap-2 justify-center flex-wrap">
          <button type="button"
                  onClick={() => navigate("/guild", { replace: true })}
                  data-testid="unstick-goto-guild"
                  className="px-4 py-2 rounded bg-neutral-900 hover:bg-neutral-800 text-amber-200 font-bold text-sm">
            Go to Guild Hall
          </button>
          <button type="button"
                  onClick={() => navigate("/town-hall", { replace: true })}
                  data-testid="unstick-goto-townhall"
                  className="px-4 py-2 rounded bg-neutral-900 hover:bg-neutral-800 text-amber-200 font-bold text-sm">
            ← Town Hall
          </button>
          {status === "error" && (
            <button type="button"
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 rounded bg-red-800 hover:bg-red-700 text-white font-bold text-sm">
              Try again
            </button>
          )}
        </div>
        {status === "working" && (
          <p className="text-[11px] opacity-60 mt-5">
            This bypasses modals and overlays. Hang tight…
          </p>
        )}
      </div>
    </div>
  );
}
